import React, { useState, useEffect } from 'react';
import './ContactModal.css';
import { X, Send, Phone, MapPin, Mail, CheckCircle, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ContactModal({ onClose }) {
  const [form, setForm] = useState({ email: '', phone: '', address: '', message: '' });
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone input, only allow numeric values
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, ''); // Remove all non-digit characters
      setForm({ ...form, [name]: numericValue });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async () => {
    if (!form.email || !form.message) {
      toast.error('Please provide your email and message.');
      return;
    }

    try {
      setStatus('loading');

      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        toast.success("Email sent successfully! We'll get back to you soon.");
        setForm({ email: '', phone: '', address: '', message: '' });
        onClose();
      } else {
        toast.error(data.error || 'Unable to send message.');
      }

    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setStatus('idle');
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" role="dialog" aria-modal="true" aria-labelledby="modal-title">

        <button className="modal-close" onClick={onClose} aria-label="Close modal">
          <X size={16} strokeWidth={2.5} />
        </button>

        {status === 'success' ? (
          <div className="success-state">
            <div className="success-icon">
              <CheckCircle size={40} color="white" strokeWidth={2.5} />
            </div>
            <h2>Message Sent!</h2>
            <p>Thank you for reaching out. We'll get back to you soon.</p>
            <button className="send-btn" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <span className="modal-tag">
                CONTACT INFORMATION
                <Send size={14} strokeWidth={2} />
              </span>
              <h2 id="modal-title" className="modal-title">We're Here to Help</h2>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Your Email</label>
                <div className="input-wrap">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Your Email"
                    value={form.email}
                    onChange={handleChange}
                  />
                  <Mail className="input-icon" size={18} strokeWidth={1.8} />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Your Phone</label>
                <div className="input-wrap">
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="Your Phone"
                    value={form.phone}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                  />
                  <Phone className="input-icon" size={18} strokeWidth={1.8} />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address">Your Address</label>
              <div className="input-wrap">
                <input
                  id="address"
                  name="address"
                  type="text"
                  placeholder="Your Address"
                  value={form.address}
                  onChange={handleChange}
                />
                <MapPin className="input-icon" size={18} strokeWidth={1.8} />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="message">Message</label>
              <div className="input-wrap textarea-wrap">
                <textarea
                  id="message"
                  name="message"
                  placeholder="Write Message.."
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                />
                <MessageSquare className="input-icon textarea-icon" size={18} strokeWidth={1.8} />
              </div>
            </div>


            <button
              className="send-btn"
              onClick={handleSubmit}
              disabled={status === 'loading'}
            >
              {status === 'loading' ? (
                <span className="spinner-wrap">
                  <span className="spinner" />
                  Sending...
                </span>
              ) : 'Send Message'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}